import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { fmtBkk } from '../../utils/bkkTime';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { format, isThisYear, isToday } from 'date-fns';
import { Attachment, Email, EmailDensity } from '../../types';
import ComposeNew from '../ComposeNew';
import AddToLeadModal from '../Leads/AddToLeadModal';
import { fileIcon, AttachmentPreview } from '../EmailDetail';
import { EMAIL_DRAG_TYPE } from '../../utils/emailDrag';
import { ACCOUNT_COLORS, tint } from '../../utils/accountColors';

const LANG_COLORS: Record<string, { bg: string; color: string }> = {
  en: { bg: '#E6F1FB', color: '#185FA5' }, ja: { bg: '#FAEEDA', color: '#854F0B' },
  de: { bg: '#EAF3DE', color: '#3B6D11' }, fr: { bg: '#FAECE7', color: '#993C1D' },
  zh: { bg: '#EEEDFE', color: '#534AB7' }, ko: { bg: '#FBEAF0', color: '#72243E' },
  th: { bg: '#FAEEDA', color: '#633806' },
};

// Today: the time alone. Yesterday and earlier: the date AND the time, on the same clock as the
// reading pane — no year for this year's dates ('22/09 09:46'), the year only for another year's
// ('30/12/2025 09:46'). Calem 2026-09-24 (Bugify #594776d4, screenshot-8: "No need to include the
// year unless it's a different year from the current year"): at every list width — this year's year
// used to drop only once the list was narrower than 320px (split screen) or 640px (one-line rows).
// Day first, UK order (Calem, Bugify #dc76fc2f: "All dates in the system … in UK date format, not US").
function formatDate(date: Date): string {
  if (isToday(date)) return format(date, 'HH:mm');
  return format(date, isThisYear(date) ? 'dd/MM HH:mm' : 'dd/MM/yyyy HH:mm');
}
// A snooze's wake-up moment is chosen in Bangkok time (EmailDetail), so it is shown in Bangkok time
// too — not the browser's zone like the received dates above.
const formatBkk = (date: Date, lang: string) => fmtBkk(date.getTime(), lang);

// The list's unread rows wear their mailbox's own colour, the one on its badge in the sidebar, as
// the search bubble and the "+" wear the open mailbox's: washed with it, their left bar, dot,
// thread count and date in it. Calem 2026-10-01 (Bugify #87ed6d43: "Change the email theme color
// to the email client color as highlighted below", the highlight on the mailbox's badge): report
// a903bda5 had put all of them in the top menu's Email purple (#7F77DD) whichever mailbox was open.
// A plain read row has no colour of its own: it is see-through, so the list's background shows, as
// it does under the last row (Calem, #87ed6d43's 2nd opening: "Remove the background blue (and
// other email client colors) color from read emails. Make the background the same as the
// background of the page"). Its 1st opening had laid a pale wash of the mailbox's colour on it, and
// a903bda5 the Email purple's wash two shades lighter (rowTint). Open, ticked, lead, starred and
// archived rows keep their own colours, as they are coloured for a reason (see bg below).

function EmailItem({
  email, isActive, isDrafts, isSnoozedView = false, onEditDraft, selected, focused, selectMode, onToggleSelect, dragIds, splitScreen, density, onPreviewAtt,
}: {
  email: Email; isActive: boolean; isDrafts: boolean; isSnoozedView?: boolean; onEditDraft: (email: Email) => void;
  selected: boolean; focused: boolean; selectMode: boolean; onToggleSelect: (id: string, shift: boolean) => void;
  dragIds: (id: string) => string[]; splitScreen: boolean; density: EmailDensity;
  onPreviewAtt: (p: { emailId: string; att: Attachment }) => void;
}) {
  const { setActiveEmail, markRead, markUnread, setStar, deleteEmails, showToast, me, loadEmails, accounts, uiLang } = useStore();
  const { t } = useTranslation();
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const lc = LANG_COLORS[email.lang] || { bg: '#F1EFE8', color: '#5F5E5A' };
  const [hovered, setHovered] = useState(false);
  const [addLead, setAddLead] = useState(false);

  const unread = email.unread && !isDrafts;
  const starred = email.starred;
  const isLead = !!email.lead;
  const isArchive = email.folder === 'archive';
  // The row's mailbox colour — the one on its badge in the sidebar (teal if the mailbox isn't in the
  // viewer's list).
  const acct = accounts.find(a => a.id === email.accountId);
  const acctColor = ((acct && ACCOUNT_COLORS[acct.color]) || ACCOUNT_COLORS.teal).accent;
  const [leadPop, setLeadPop] = useState<{ x: number; y: number } | null>(null);

  // Track click timing to distinguish single vs double click
  const clickTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (isDrafts) {
      if (clickTimer.current) clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => setActiveEmail(email.id), 200);
    } else {
      setActiveEmail(email.id);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDrafts) {
      if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
      onEditDraft(email);
    }
  };

  // Colour priority: active > unread > lead (purple) > starred (amber) > archive (gray) > normal.
  const bg = isActive
    ? 'var(--bg-active)'
    : unread
      ? tint(acctColor, 0.08)          // mailbox-colour tint for unread
      : isLead
        ? 'rgba(109,40,217,0.07)'      // purple tint for lead emails
        : starred
          ? 'rgba(245,166,35,0.07)'    // amber tint for starred
          : isArchive
            ? 'rgba(107,114,128,0.07)' // gray tint for archived
            : 'transparent';           // read: no tint, the list's background shows
  // Left bar: the open row's and a lead's are the row's own mailbox colour, the one on its unread
  // dot; a starred row's amber takes precedence over unread so it stays identifiable at a glance.
  // Calem, Bugify #722b880d ("Shouldn't this be green?"): the open row's bar was var(--accent), the
  // colour of the mailbox open in the sidebar. Its 3rd opening: both rows circled in screenshot-3
  // are leads ("The purple colored vertical bar highlighted in the picture should be green for that
  // email address"); a lead's bar was the Leads purple #6D28D9, an unread lead's too. A lead still
  // stands out by its purple target icon and, once read, its purple wash (bg above). A ticked row's
  // bar stays var(--accent) (the row's boxShadow below).
  const leftBar = isActive || isLead
    ? acctColor
    : starred
      ? '#F5A623'
      : unread
        ? acctColor
        : isArchive
          ? '#9CA3AF'
          : 'transparent';
  // The row's fill: a ticked row's accent wash, else the tint above. The hover buttons (split
  // screen off) sit on a strip of the same colour, so the two can't drift apart.
  const rowBg = selected ? 'color-mix(in srgb, var(--accent) 16%, transparent)' : bg;
  const LEAD_ST: Record<string, { bg: string; color: string; label: string }> = {
    open: { bg: '#E4F0FB', color: '#2B6CB0', label: 'Open' },
    won: { bg: '#DCFCE7', color: '#15803D', label: 'Won' },
    lost: { bg: '#FCE7E7', color: '#B91C1C', label: 'Lost' },
  };
  const lst = (email.lead && LEAD_ST[email.lead.status]) || LEAD_ST.open;

  const showCheckbox = !isDrafts && (selectMode || selected || hovered);
  const langBadge = !isDrafts && (
    <span style={{ display: 'inline-flex', fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, marginRight: 5, letterSpacing: '0.04em', background: lc.bg, color: lc.color }}>{email.lang.toUpperCase()}</span>
  );
  const subject = email.subject || '(No subject)';
  const draftHint = <><i className="ti ti-pencil" style={{ fontSize: 10 }} aria-hidden />Double-click to continue editing</>;
  const preview = !isDrafts && ((email as any).preview || email.bodyOriginal.replace(/<[^>]+>/g, '').slice(0, 80));
  // Split screen off: the hover buttons end where the Subject column ends, just before the Body.
  // That column is a share of the row's width and shifts with the tick boxes' indent, so its right
  // edge is read off the page on hover (and again if the indent comes or goes meanwhile).
  const subjectRef = useRef<HTMLDivElement>(null);
  const [subjectEnd, setSubjectEnd] = useState(0);
  useLayoutEffect(() => {
    const s = subjectRef.current;
    if (hovered && s) setSubjectEnd(s.offsetLeft + s.offsetWidth);
  }, [hovered, selectMode, splitScreen]);
  // Row padding (top/bottom), by the toolbar's Density. Default: enough air between rows for the
  // larger text, plus 1px each side (Calem 2026-09-24, Bugify #594776d4); the one-line full-width
  // list a little tighter than the three-line column. Comfortable: 4px more above and below.
  // Compact: the least that still holds the 24px hover buttons in a one-line row — and the
  // split-screen row drops its preview line (below).
  const padY = density === 'comfortable' ? (splitScreen ? 14 : 12) : density === 'compact' ? 4 : (splitScreen ? 10 : 8);
  // Default density also lists the email's attachments as chips on a line of their own under the
  // text (Gmail-style): 4px gap + 24px chips. Comfortable and Compact keep just the paperclip.
  const showChips = density === 'default' && email.attachments.length > 0;
  const chipsH = showChips ? 28 : 0;
  // In the one-line row the tick box and the hover buttons sit in the vertical centre of its text
  // line (the chips line, if any, below it); in the three-line row they follow padY, level with the
  // sender line and the preview line (the subject in Compact) respectively — never over the chips.
  const midRow = { top: chipsH ? `calc(50% - ${chipsH / 2}px)` : '50%', transform: 'translateY(-50%)' };
  return (
    <div
      id={'eml-' + email.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative' }}
    >
      {/* Selection checkbox (appears on hover / once any row is selected). */}
      {showCheckbox && (
        <span
          onClick={e => { e.stopPropagation(); onToggleSelect(email.id, (e as any).shiftKey); }}
          title="Select"
          style={{ position: 'absolute', left: 11, ...(splitScreen ? { top: padY + 2 } : midRow), zIndex: 3, width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border-strong)'}`, background: selected ? 'var(--accent)' : 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          {selected && <i className="ti ti-check" style={{ fontSize: 11, color: '#fff' }} aria-hidden />}
        </span>
      )}
      {/* Draggable onto a label in the sidebar to apply it. Set on this button rather than the
          wrapper, which also holds the inline Add-to-Lead modal. Drafts have no row actions. */}
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        draggable={!isDrafts}
        onDragStart={isDrafts ? undefined : e => {
          e.dataTransfer.setData(EMAIL_DRAG_TYPE, JSON.stringify(dragIds(email.id)));
          e.dataTransfer.effectAllowed = 'copy';
        }}
        title={isDrafts ? 'Double-click to edit draft' : undefined}
        style={{
          display: 'block', width: '100%', padding: `${padY}px 14px ${padY}px ${selectMode ? 34 : 16}px`,
          border: 'none',
          borderBottom: '1px solid var(--border)',
          background: rowBg,
          boxShadow: `inset 3px 0 0 ${selected ? 'var(--accent)' : leftBar}${focused ? ', inset 0 0 0 2px var(--accent)' : ''}`,
          cursor: 'pointer',
          textAlign: 'left' as const, position: 'relative' as const,
        }}
      >
        {/* Split screen on: sender + time above the subject and preview lines. Split screen off
            (full-width list): the whole email is ONE line — From, Subject and Body as columns kept
            apart by space alone (no separator lines), then the icons + time. Its hover buttons cover
            the right end of the Subject column (below). */}
        <div style={splitScreen
          ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }
          : { display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, ...(splitScreen ? {} : { width: 190, flexShrink: 0 }) }}>
            {/* Leading dot on every row, so all senders start at the same point: the mailbox colour
                when unread, light grey once read. Drafts have no read state, so no dot. */}
            {!isDrafts && <span style={{ width: 8, height: 8, borderRadius: '50%', background: unread ? acctColor : 'var(--border-strong)', flexShrink: 0 }} aria-label={unread ? 'unread' : undefined} />}
            {isDrafts && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#FFF3CD', color: '#7B5200', border: '1px solid #F5C842', letterSpacing: '0.04em' }}>DRAFT</span>
            )}
            <span style={{ fontSize: 14.5, fontWeight: unread ? 700 : (isDrafts ? 600 : 500), color: unread ? 'var(--text-primary)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts ? (email.to.join(', ') || 'No recipients')
                : ((email.threadCount || 1) > 1 && email.participants?.length
                    ? email.participants.map(p => (p.name || p.email).split(' ')[0]).join(', ')
                    : (email.from.name || email.from.email))}
            </span>
            {/* Conversation size — Gmail-style count next to the participants */}
            {!isDrafts && (email.threadCount || 1) > 1 && (
              <span style={{ fontSize: 11.5, fontWeight: 600, color: unread ? acctColor : 'var(--text-tertiary)', flexShrink: 0 }}>{email.threadCount}</span>
            )}
          </div>
          {!splitScreen && (<>
            {/* A fixed share of the row, so every row's Body column starts at the same point */}
            <div ref={subjectRef} style={{ flex: '0 1 32%', minWidth: 0, fontSize: 13.5, fontWeight: unread ? 700 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {langBadge}
              {subject}
            </div>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{draftHint}</span>
                : preview
              }
            </div>
          </>)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {isLead && (
              <span
                onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setLeadPop({ x: r.right, y: r.bottom }); }}
                onMouseLeave={() => setLeadPop(null)}
                onClick={e => { e.stopPropagation(); setLeadPop(null); setAddLead(true); }}
                title={t('ld_change_or_remove')}
                style={{ display: 'inline-flex', cursor: 'pointer' }}
              >
                <i className="ti ti-target-arrow" style={{ fontSize: 13, color: '#6D28D9' }} aria-hidden />
              </span>
            )}
            {starred && <i className="ti ti-star-filled" style={{ fontSize: 13, color: '#F5A623' }} aria-hidden />}
            {email.attachments.length > 0 && <i className="ti ti-paperclip" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />}
            {/* Snoozed folder: the time it comes BACK, not the received date (owner review 2026-09-24). */}
            {isSnoozedView && email.snoozedUntil
              ? <span title={t('eml_snooze_returns')} style={{ fontSize: 10, color: '#B45309', fontWeight: 600, whiteSpace: 'nowrap' }}><i className="ti ti-clock-pause" style={{ fontSize: 10 }} aria-hidden /> {formatBkk(email.snoozedUntil, uiLang)}</span>
              : <span style={{ fontSize: 10, color: unread ? acctColor : 'var(--text-tertiary)', fontWeight: unread ? 600 : 400, whiteSpace: 'nowrap' }}>{formatDate(email.date)}</span>}
          </div>
        </div>
        {splitScreen && (<>
          <div style={{ fontSize: 13.5, color: unread ? 'var(--text-primary)' : 'var(--text-secondary)', marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: unread ? 600 : (isDrafts ? 500 : 400) }}>
            {langBadge}
            {subject}
          </div>
          {/* Compact density: sender and subject only — no preview line */}
          {density !== 'compact' && (
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{draftHint}</span>
                : preview
              }
            </div>
          )}
        </>)}
        {/* Attachment chips (Default density): under the preview line, or in the one-line row under
            its Subject and Body columns (past the 190px From column + 16px gap). The first three by
            name, then '+N'. A chip previews its file over the page, with a Download button there
            (Calem, Bugify #e0e30d4b: preview, don't download); '+N' just opens the email like the row. */}
        {showChips && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, height: 24, overflow: 'hidden', paddingLeft: splitScreen ? 0 : 190 + 16 }}>
            {email.attachments.slice(0, 3).map(att => (
              <span
                key={att.id}
                onClick={e => { e.stopPropagation(); onPreviewAtt({ emailId: email.id, att }); }}
                title={t('eml_att_preview', { name: att.name })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, minWidth: 0, maxWidth: 180, padding: '0 10px 0 8px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', cursor: 'pointer' }}
              >
                <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</span>
              </span>
            ))}
            {email.attachments.length > 3 && (
              <span title={t('eml_att_more', { n: email.attachments.length - 3 })} style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 9px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>
                +{email.attachments.length - 3}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Hover quick-actions: read/unread toggle, star, (lead,) delete. Split screen on: at the
          right end, level with the row's preview line. Off: over the right end of the Subject
          column, centred on the one-line row's text, on a strip of the row's own colour (laid over
          the list's panel, as the row's tints are see-through) so the subject's end is hidden
          cleanly rather than showing between the buttons. Both above the attachment chips, so the
          chips stay clickable. */}
      {hovered && !isDrafts && (
        <div style={{ position: 'absolute', display: 'flex', gap: 4, ...(splitScreen
          ? { right: 10, bottom: padY - 3 + chipsH }
          : { ...midRow, right: `calc(100% - ${subjectEnd}px)`, paddingLeft: 8, backgroundColor: 'var(--bg-panel)', backgroundImage: `linear-gradient(${rowBg}, ${rowBg})` }) }}>
          <button
            onClick={e => { e.stopPropagation(); if (email.unread) { (email.threadIds?.length ? email.threadIds : [email.id]).forEach(id => markRead(id)); } else { markUnread(email.id); } }}
            title={email.unread ? 'Mark as read' : 'Mark as unread'}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className={`ti ${email.unread ? 'ti-mail-opened' : 'ti-mail'}`} style={{ fontSize: 12 }} aria-hidden />
          </button>
          <button
            onClick={e => { e.stopPropagation(); const ids = email.threadIds?.length ? email.threadIds : [email.id]; const on = !starred; ids.forEach(id => setStar(id, on)); }}
            title={starred ? 'Unstar' : 'Star'}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: starred ? '#F5A623' : 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className={`ti ti-star${starred ? '-filled' : ''}`} style={{ fontSize: 12 }} aria-hidden />
          </button>
          {canLead && (
            <button
              onClick={e => { e.stopPropagation(); setAddLead(true); }}
              title={isLead ? t('ld_change_or_remove') : t('ld_add_to')}
              style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: '#6D28D9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
            >
              <i className="ti ti-target-arrow" style={{ fontSize: 12 }} aria-hidden />
            </button>
          )}
          {/* Delete the whole conversation — same confirm as the bulk Delete; the row leaves the
              list at once and comes back only if the server can't delete it (see store.deleteEmails). */}
          <button
            onClick={e => { e.stopPropagation(); const ids = email.threadIds?.length ? email.threadIds : [email.id]; if (window.confirm(t('em_del_confirm', { n: ids.length }))) void deleteEmails(ids); }}
            title={t('delete')}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: '#E24B4A', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className="ti ti-trash" style={{ fontSize: 12 }} aria-hidden />
          </button>
        </div>
      )}
      {addLead && <AddToLeadModal email={{ id: email.id, subject: email.subject, fromName: email.from.name || '', fromEmail: email.from.email || '', date: email.date, bodyText: (email as any).preview || email.bodyOriginal || '' }}
        current={email.lead ? { rowId: email.lead.rowId, id: email.lead.id, name: email.lead.name, company: email.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {/* Lead hover card — basic info; fixed-position so it isn't clipped by the list scroll area. */}
      {leadPop && email.lead && (
        <div style={{ position: 'fixed', left: Math.min(leadPop.x, window.innerWidth - 256), top: leadPop.y + 5, zIndex: 4000, width: 244, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: '10px 12px', fontSize: 12.5, fontFamily: 'inherit', pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <i className="ti ti-target-arrow" style={{ fontSize: 13, color: '#6D28D9' }} aria-hidden />
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.lead.name || email.lead.company || '—'}</span>
            <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: lst.bg, color: lst.color, flexShrink: 0 }}>{lst.label}</span>
          </div>
          {email.lead.company && email.lead.name && <div style={{ color: 'var(--text-secondary)', marginBottom: 2 }}>{email.lead.company}</div>}
          {email.lead.owner_name && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}><i className="ti ti-user" style={{ fontSize: 11 }} aria-hidden />{email.lead.owner_name}</div>}
          {email.lead.phone && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}><i className="ti ti-phone" style={{ fontSize: 11 }} aria-hidden />{email.lead.phone}</div>}
          {email.lead.email && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5, wordBreak: 'break-all' }}><i className="ti ti-mail" style={{ fontSize: 11 }} aria-hidden />{email.lead.email}</div>}
          <div style={{ color: 'var(--text-tertiary)', marginTop: 4, fontSize: 11, opacity: 0.8 }}>{t('ld_source')}: {email.lead.source}</div>
        </div>
      )}
    </div>
  );
}

// The bar that heads a group of rows in the list: its word in capitals, 13px, between two lines in
// --border-strong. UNREAD sits above the unread group, EVERYTHING ELSE between it and the read rows.
// Calem (Bugify #810a6232: "Make the gray line one shade darker and change the word "read" to
// "everything else" remaining in capitals. Add another bar like this above the unread section …
// Increase the size of the words … by 2pt"): there was one bar, READ, between the two groups only —
// 10.5px words between --border lines.
function GroupBar({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--border-strong)' }} />
      {label}
      <span style={{ flex: 1, height: 1, background: 'var(--border-strong)' }} />
    </div>
  );
}

export default function EmailList({ splitScreen, onSetSplitScreen, toolbarHost }: { splitScreen: boolean; onSetSplitScreen: (v: boolean) => void; toolbarHost?: HTMLElement | null }) {
  const { t } = useTranslation();
  const { emails: rawEmails, totalEmails, isLoadingMore, loadMoreEmails, activeEmailId, activeAccountId, activeFolder, searchQuery, isLoading,
          setSearchQuery, loadEmails, loadEmail, conversationView, setConversationView, emailDensity, setEmailDensity,
          setActiveEmail, archiveEmail, deleteEmail, deleteEmails, markRead, markUnread, toggleStar, setStar, me, labelOverrides } = useStore();
  const [editingDraft, setEditingDraft] = useState<Email | null>(null);
  // The attachment chip whose file is being previewed over the page, if any.
  const [previewAtt, setPreviewAtt] = useState<{ emailId: string; att: Attachment } | null>(null);
  const isDraftsView = activeFolder === 'drafts';
  // Snoozed keeps the server's order (soonest wake-up first) — no unread-first regrouping there.
  const isSnoozedView = activeFolder === 'snoozed';

  // Merged Unread/All view: unread messages float to the top of the normal list (in their existing
  // relative order), read ones sit below a separator. The store loads EVERY unread row of the view
  // along with the page (loadEmails), so this group holds what the sidebar badge counts, not only
  // the unread among the newest 50. Drafts keep their natural order (no meaningful unread state).
  const keepOrder = isDraftsView || isSnoozedView;
  const unreadEmails = useMemo(() => keepOrder ? [] : rawEmails.filter(e => e.unread), [rawEmails, keepOrder]);
  const readEmails = useMemo(() => keepOrder ? [] : rawEmails.filter(e => !e.unread), [rawEmails, keepOrder]);
  const emails = useMemo(
    () => (keepOrder || !unreadEmails.length || !readEmails.length) ? rawEmails : [...unreadEmails, ...readEmails],
    [keepOrder, unreadEmails, readEmails, rawEmails],
  );

  // ── Multi-select + keyboard ────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [bulkLead, setBulkLead] = useState(false);
  const lastClickedRef = useRef<string | null>(null);
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const selectMode = selected.size > 0;
  const allSelected = emails.length > 0 && selected.size === emails.length;

  // Reset selection/focus whenever the folder or account changes.
  useEffect(() => { setSelected(new Set()); setFocusedId(null); lastClickedRef.current = null; }, [activeFolder, activeAccountId]);
  // Drop ids that no longer exist after a reload (e.g. archived rows leave the view).
  useEffect(() => { setSelected(prev => { if (!prev.size) return prev; const live = new Set(emails.map(e => e.id)); const n = new Set(Array.from(prev).filter(id => live.has(id))); return n.size === prev.size ? prev : n; }); }, [emails]);

  const toggleSelect = (id: string, shift: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (shift && lastClickedRef.current) {
        const ids = emails.map(e => e.id);
        const a = ids.indexOf(lastClickedRef.current), b = ids.indexOf(id);
        if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; for (let i = lo; i <= hi; i++) next.add(ids[i]); }
      } else { next.has(id) ? next.delete(id) : next.add(id); }
      return next;
    });
    lastClickedRef.current = id;
    setFocusedId(id);
  };
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(allSelected ? new Set() : new Set(emails.map(e => e.id)));
  // Apply an action to the selection (or, if none selected, the keyboard-focused email).
  // Row ids → every email id in those conversations (list actions act on the WHOLE thread).
  const expandThreads = (rowIds: string[]): string[] => {
    const map = new Map(emails.map(e => [e.id, e] as const));
    return rowIds.flatMap(id => { const r = map.get(id); return r?.threadIds?.length ? r.threadIds : [id]; });
  };
  const applyTo = async (fn: (id: string) => Promise<void>, ids?: string[]) => {
    const rows = ids || (selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []));
    if (!rows.length) return;
    const targets = expandThreads(rows);
    for (const id of targets) { try { await fn(id); } catch { /* keep going */ } }
    clearSel();
  };
  // Drag-to-label: dragging a row that's part of the selection carries the whole selection, any
  // other row just itself — expanded to every message of each conversation, like the actions above.
  const dragIds = (id: string) => Array.from(new Set(expandThreads(selected.has(id) ? Array.from(selected) : [id])));
  // Delete = confirm once → rows leave the list at once; any the server can't delete come back (see
  // store.deleteEmails). Errors surface only as a non-blocking toast.
  const bulkDelete = () => {
    const rows = selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []);
    if (!rows.length) return;
    const targets = expandThreads(rows);   // deleting a conversation deletes every message in it
    if (!window.confirm(t('em_del_confirm', { n: targets.length }))) return;
    clearSel();
    void deleteEmails(targets);   // fire-and-forget — the store takes the rows out at once
  };
  const bulkStar = async () => {
    const rows = selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []);
    const map = new Map(emails.map(e => [e.id, e] as const));
    const on = rows.some(id => !map.get(id)?.starred);   // any unstarred → star them all
    for (const id of expandThreads(rows)) { try { await setStar(id, on); } catch { /* ignore */ } }
    clearSel();
  };

  // Keyboard shortcuts — active when the email module is mounted and focus isn't in a field/modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (!el || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
      // A preview open over the list: its keys (Esc closes it) must not archive / delete rows behind it.
      if (editingDraft || bulkLead || previewAtt) return;
      if (!emails.length) return;
      const ids = emails.map(x => x.id);
      const curIdx = focusedId ? ids.indexOf(focusedId) : -1;
      const moveFocus = (delta: number, extend: boolean) => {
        const ni = curIdx < 0 ? (delta > 0 ? 0 : ids.length - 1) : Math.min(ids.length - 1, Math.max(0, curIdx + delta));
        const nid = ids[ni];
        if (extend && focusedId) { setSelected(prev => { const n = new Set(prev); n.add(focusedId); n.add(nid); return n; }); }
        setFocusedId(nid);
        document.getElementById('eml-' + nid)?.scrollIntoView({ block: 'nearest' });
      };
      const k = e.key;
      if (k === 'ArrowDown' || k === 'j') { e.preventDefault(); moveFocus(1, e.shiftKey); }
      else if (k === 'ArrowUp' || k === 'k') { e.preventDefault(); moveFocus(-1, e.shiftKey); }
      else if (k === 'Enter' || k === 'o') { if (focusedId) setActiveEmail(focusedId); }
      else if (k === 'x') { e.preventDefault(); if (focusedId) toggleSelect(focusedId, false); }
      else if ((k === 'a' || k === 'A') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); selectAll(); }
      else if (k === 'Escape') { clearSel(); }
      else if (isDraftsView) { /* no row actions in Drafts */ }
      else if (k === 'e') { e.preventDefault(); applyTo(archiveEmail); }
      else if (k === '#' || k === 'Delete' || k === 'Backspace') { e.preventDefault(); bulkDelete(); }
      else if (k === 's') { e.preventDefault(); bulkStar(); }
      else if (k === 'I') { e.preventDefault(); applyTo(markRead); }
      else if (k === 'U') { e.preventDefault(); applyTo(markUnread); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [emails, focusedId, selected, editingDraft, bulkLead, previewAtt, isDraftsView]); // eslint-disable-line

  const selectedEmails = emails.filter(e => selected.has(e.id));

  // Open a draft for editing — fetch the FULL body first (list only has a preview)
  const openDraft = async (email: Email) => {
    const full = await loadEmail(email.id);
    setEditingDraft(full || email);
  };

  const isDrafts = activeFolder === 'drafts';
  // Drafts/Trash/Snoozed are always shown flat (never threaded) — the toggle would have nothing to do
  // there. Snoozed keeps the server's order: soonest wake-up first.
  const canGroupThreads = !isDrafts && activeFolder !== 'trash' && activeFolder !== 'snoozed';
  // The select-all checkbox + its caption stand where the folder name used to; the folder name
  // only remains where there's nothing to select (Drafts, an empty list).
  const canSelectAll = !isDrafts && emails.length > 0;
  const folderLabel: Record<string, string> = {
    inbox: 'Inbox', sent: 'Sent', starred: 'Starred',
    archive: 'Archive', drafts: 'Drafts', trash: 'Trash', spam: t('spam'), snoozed: t('eml_snoozed'), scheduled: t('eml_scheduled'),
  };
  // The bulk-action icons are 17px, about 15% larger than the 15px they were (Calem, report
  // 30e4962a: "Make these icons slightly (say 15%) larger"). The 30×30 buttons keep their size, so
  // the toolbar's height and the spacing between the buttons don't change.
  const bulkBtn = (icon: string, title: string, onClick: () => void, color = 'var(--text-secondary)') => (
    <button onClick={onClick} title={title} style={{ width: 30, height: 30, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 17 }} aria-hidden />
    </button>
  );

  // The toolbar: select-all, the Split screen / Conversation view / Density switches, Search and the
  // bulk actions. With split screen on it is drawn in MailApp's strip across the top of the list AND
  // the reading pane (toolbarHost), full width as with split screen off, instead of squeezed onto two
  // rows at the top of this column (Calem, Bugify #e0e30d4b); only where it is drawn moves — the
  // selection, the search and the bulk actions stay in this list. The strip stays put while split
  // screen is on, so Search keeps its focus while typing. It carries its colour itself, since in the
  // strip it no longer sits on the list: the light grey, --c-gray-50, as File Storage's toolbar band
  // is and every top-menu page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip
  // on every page turns that same grey then the rest of the page below this menu has a white
  // background"). It was the list's panel colour, --bg-panel, a hair darker. Its line is a shade
  // darker, --border-strong where it was --border, as is the top menu's over it (Calem, report
  // a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on all page menus like
  // this").
  const toolbar = (
    <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, rowGap: 6, borderBottom: '1px solid var(--border-strong)', minHeight: 52, boxSizing: 'border-box', background: 'var(--c-gray-50)' }}>
      {/* Select-all checkbox + its clickable caption (not in Drafts) */}
      {canSelectAll && (<>
        <span onClick={selectAll} title={allSelected ? 'Deselect all' : 'Select all (Ctrl/⌘+A)'} style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selected.size ? 'var(--accent)' : 'var(--border-strong)'}`, background: selected.size ? 'var(--accent)' : 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          {selected.size > 0 && <i className={`ti ti-${allSelected ? 'check' : 'minus'}`} style={{ fontSize: 11, color: '#fff' }} aria-hidden />}
        </span>
        <span onClick={selectAll} style={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1, color: 'var(--text-primary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{t(allSelected ? 'eml_deselect_all' : 'eml_select_all')}</span>
      </>)}
      {selectMode ? (
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)' }}>{selected.size} {t('eml_selected')}</span>
      ) : (
        <>
          {!canSelectAll && <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{folderLabel[activeFolder] || labelOverrides[activeAccountId || '']?.[activeFolder]?.name || activeFolder}</span>}
          {/* Split screen on/off */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_split_screen')}</span>
            <div title={t('eml_split_screen')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => (
                <button key={String(val)} onClick={() => onSetSplitScreen(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: splitScreen === val ? 'var(--accent)' : 'transparent', color: splitScreen === val ? '#fff' : 'var(--text-secondary)' }}>
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
          {/* Conversation view on/off — on (default) groups same-thread messages into one row;
              off shows every message as its own flat row. Not shown in Drafts/Trash, which are
              always flat. Unread-first sorting + the Read separator below apply either way. */}
          {canGroupThreads && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_conv_view')}</span>
              <div title={t('eml_conv_view')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
                {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => (
                  <button key={String(val)} onClick={() => setConversationView(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: conversationView === val ? 'var(--accent)' : 'transparent', color: conversationView === val ? '#fff' : 'var(--text-secondary)' }}>
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Row density — Default, Comfortable (more air per row) or Compact (tighter rows; no
              preview line in split screen). Every folder; the choice is remembered on this browser. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_density')}</span>
            <div title={t('eml_density')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([['default', 'eml_density_default'], ['comfortable', 'eml_density_comfortable'], ['compact', 'eml_density_compact']] as const).map(([val, key]) => (
                <button key={val} onClick={() => setEmailDensity(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: emailDensity === val ? 'var(--accent)' : 'transparent', color: emailDensity === val ? '#fff' : 'var(--text-secondary)' }}>
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {/* Bulk actions as one group, so in a narrow column they wrap together — straight after the
          "n selected" count, a small gap from it, not at the right end (Calem, Bugify #a375cb1a
          screenshot-2). In front of Search, which keeps the right end of the bar. */}
      {selectMode && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginLeft: 4 }}>
          {bulkBtn('ti-archive', 'Archive (e)', () => applyTo(archiveEmail))}
          {bulkBtn('ti-trash', 'Delete (#)', () => bulkDelete(), '#E24B4A')}
          {bulkBtn('ti-mail-opened', 'Mark read (Shift+I)', () => applyTo(markRead))}
          {bulkBtn('ti-mail', 'Mark unread (Shift+U)', () => applyTo(markUnread))}
          {bulkBtn('ti-star', 'Star (s)', bulkStar, '#F5A623')}
          {canLead && bulkBtn('ti-target-arrow', t('ld_add_to'), () => setBulkLead(true), '#6D28D9')}
          {bulkBtn('ti-x', 'Clear (Esc)', clearSel)}
        </div>
      )}
      {/* Search — at the right end of the bar, at most 250px: about 30% of the room it used to fill
          after the switches (Calem, Bugify #e0e30d4b screenshot-7). Its own auto margin pushes it
          there, rows ticked or not — the bulk actions in front of it stay by the count at the left.
          It only narrows (to 160px) or drops to its own line when the bar runs short of room. No
          refresh button beside it any more (Calem, Bugify #594776d4 screenshot-12): the sidebar's
          Sync bubble syncs the open mailbox. It stays at this one spot whether rows are ticked or not
          (the bulk-action slot before it is kept even when empty): each keystroke reloads the list,
          which can untick every row mid-typing, and a remounted box would drop the focus.
          It is a round coloured bubble (Calem, Bugify #cba01676, 6th opening: "Do the same (colored bubble search)
          for quotation/invoice/receipts, email/calendar and petty cash"), dressed as Bank Statement's search box:
          round ends, a 1px line, 5px above and below and 12px semibold — in the open mailbox's colour, the one on its
          badge in the sidebar, as the reading pane's round buttons are: its pale wash (--accent-bg), its line (the
          colour at 35%) and words in it, the placeholder's too (mf-ph-inherit) — the magnifier in it 12px in and the
          words 30px in, clear of it. The clear × wears it too, and the words still stop 28px short of the right end
          for it. Calem 2026-10-01 (Bugify #87ed6d43: "Change the email theme color to the email client color"): it
          wore the top menu's Email purple (menuPill('email')) whichever mailbox was open; before #cba01676 it was an
          8px-cornered white box with the faint grey outline, its magnifier and × grey. */}
      <div style={{ position: 'relative', flex: 1, minWidth: 160, maxWidth: 250, marginLeft: 'auto' }}>
        <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--accent)' }} aria-hidden />
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('searchPlaceholder')}
          className="mf-ph-inherit"
          style={{ width: '100%', padding: '5px 28px 5px 30px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)', outline: 'none', background: 'var(--accent-bg)', color: 'var(--accent)' }}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg-panel)', overflow: 'hidden' }}>
      {splitScreen && toolbarHost ? createPortal(toolbar, toolbarHost) : toolbar}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden />
          </div>
        ) : emails.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <i className="ti ti-inbox" style={{ fontSize: 32, color: 'var(--text-tertiary)', marginBottom: 8 }} aria-hidden />
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
              {isDrafts ? 'No drafts saved' : 'No emails'}
            </div>
            {isDrafts && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Click "New Email" and save a draft to see it here
              </div>
            )}
          </div>
        ) : (
          <>
            {emails.map((email, idx) => (
              <React.Fragment key={email.id}>
                {/* Bar above the unread group, which always starts the list when there is one */}
                {!isDrafts && idx === 0 && unreadEmails.length > 0 && <GroupBar label={t('eml_unread')} />}
                {/* Separator between the unread group (above) and the read group (below) */}
                {!isDrafts && idx === unreadEmails.length && unreadEmails.length > 0 && readEmails.length > 0 && <GroupBar label={t('eml_everything_else')} />}
                <EmailItem
                  email={email}
                  isActive={email.id === activeEmailId}
                  isDrafts={isDrafts}
                  isSnoozedView={isSnoozedView}
                  onEditDraft={openDraft}
                  selected={selected.has(email.id)}
                  focused={focusedId === email.id}
                  selectMode={selectMode}
                  onToggleSelect={toggleSelect}
                  dragIds={dragIds}
                  splitScreen={splitScreen}
                  density={emailDensity}
                  onPreviewAtt={setPreviewAtt}
                />
              </React.Fragment>
            ))}
            {/* Load more — 50 at a time until every email in this view is shown. */}
            {!isDrafts && emails.length < totalEmails && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '14px 0 18px' }}>
                <button onClick={() => loadMoreEmails()} disabled={isLoadingMore}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--accent)', fontSize: 12.5, fontWeight: 600, cursor: isLoadingMore ? 'default' : 'pointer', opacity: isLoadingMore ? 0.6 : 1 }}>
                  <i className={isLoadingMore ? 'ti ti-loader' : 'ti ti-chevron-down'} style={isLoadingMore ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : undefined} aria-hidden />
                  {isLoadingMore ? t('em_loading_more') : t('em_load_more')}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('em_shown_of', { shown: emails.length, total: totalEmails })}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Open ComposeNew pre-filled with draft data */}
      {editingDraft && (
        <ComposeNew
          onClose={() => { setEditingDraft(null); loadEmails(); }}
          draft={editingDraft}
        />
      )}

      {/* An attachment chip's file, previewed over the page */}
      {previewAtt && (
        <AttachmentPreview key={previewAtt.emailId + previewAtt.att.id} emailId={previewAtt.emailId} att={previewAtt.att} onClose={() => setPreviewAtt(null)} />
      )}

      {/* Bulk: add all selected emails to one lead */}
      {bulkLead && selectedEmails.length > 0 && (
        <AddToLeadModal
          emails={selectedEmails.map(e => ({ id: e.id, subject: e.subject, fromName: e.from.name || '', fromEmail: e.from.email || '', date: e.date, bodyText: (e as any).preview || e.bodyOriginal || '' }))}
          onClose={() => setBulkLead(false)}
          onDone={() => { setBulkLead(false); clearSel(); loadEmails(); }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
